import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import moment from "moment";
import { BsCalendar2Date } from "react-icons/bs";

const FIELDS = {
  gateOutDate: ["GATEINDATE"],
  gateOutNo: ["GATEINNO"],
  containerNo: ["CONTAINERNO"],
  transport: ["TRANSPORTER"],
  bookingNo: ["BLNO", "BOOKINGNO"],
  truckNo: ["TRUCK"],
  driverName: ["DRIVENAME"],
  daysInYard: ["DAYSINYARD"],
  vesselName: ["VESSELNAME"],
};

const getField = (item, keys) => {
  for (const k of keys) {
    if (item[k] !== undefined && item[k] !== null && item[k] !== "") {
      return item[k];
    }
  }
  return "";
};

// View format: "2026-05-15T00:00:00.000Z" -> "2026-05-15" (no timezone shift)
const toYMD = (value) => {
  if (!value) return "";
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const m = moment(str, "DD/MM/YYYY", true);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

const displayDate = (ymd) =>
  ymd ? moment(ymd, "YYYY-MM-DD").format("DD/MM/YYYY") : "";

const DATE_FMT = "YYYY-MM-DD";

const getPresetRange = (filter) => {
  const today = moment();
  switch (filter) {
    case "last3days":
      return [today.clone().subtract(3, "days"), today];
    case "last7days":
      return [today.clone().subtract(7, "days"), today];
    case "last30days":
      return [today.clone().subtract(30, "days"), today];
    case "lastMonth": {
      const m = today.clone().subtract(1, "month");
      return [m.clone().startOf("month"), m.clone().endOf("month")];
    }
    case "lastQuarter": {
      const q = today.clone().subtract(1, "quarter");
      return [q.clone().startOf("quarter"), q.clone().endOf("quarter")];
    }
    case "lastYear": {
      const y = today.clone().subtract(1, "year");
      return [y.clone().startOf("year"), y.clone().endOf("year")];
    }
    default:
      return null;
  }
};

const DepotGateInwards = () => {
  const BASEURL_GLOBAL = process.env.REACT_APP_BASEURL_GLOBAL;
  const authToken = window.sessionStorage.getItem("auth-token");
  const sessiontoken = window.sessionStorage.getItem("session-token");

  // Dates are plain "YYYY-MM-DD" strings; default = 1st of this month -> today
  const [fromdate, setFromdate] = useState(
    moment().startOf("month").format(DATE_FMT)
  );
  const [todate, setTodate] = useState(moment().format(DATE_FMT));
  const [activeDates, setActiveDates] = useState(false);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const filterRef = useRef(null);

  // Close the date popup when clicking outside it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setActiveDates(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load the full view once; dates only filter the loaded data on the client
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(
          `${BASEURL_GLOBAL}/globalViewHandler?viewname=1878`,
          {
            headers: {
              "auth-token": authToken,
              "session-token": sessiontoken,
            },
          }
        );
        if (!cancelled) {
          setAnalyticsData(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        console.error("Error fetching depot outward interchange:", err);
        if (!cancelled) {
          setError("Failed to load data. Please try again.");
          setAnalyticsData([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [BASEURL_GLOBAL, authToken, sessiontoken]);

  const invalidRange =
    !!fromdate && !!todate && moment(fromdate).isAfter(moment(todate));

  const handleDateFilterChange = useCallback((filter) => {
    const range = getPresetRange(filter);
    if (!range) return;
    setFromdate(range[0].format(DATE_FMT));
    setTodate(range[1].format(DATE_FMT));
    setActiveDates(false);
  }, []);

  // Filter by date range + search, then group by gate-out date
  const groups = useMemo(() => {
    const map = new Map();
    if (!fromdate || !todate || fromdate > todate) return [];
    const terms = searchText.toLowerCase().split(/\s+/).filter(Boolean);

    analyticsData.forEach((item) => {
      const gateOut = toYMD(getField(item, FIELDS.gateOutDate));
      if (!gateOut || gateOut < fromdate || gateOut > todate) return;

      const row = {
        gateOut,
        gateOutNo: getField(item, FIELDS.gateOutNo),
        containerNo: getField(item, FIELDS.containerNo),
        transport: getField(item, FIELDS.transport),
        bookingNo: getField(item, FIELDS.bookingNo),
        truckNo: getField(item, FIELDS.truckNo),
        driverName: getField(item, FIELDS.driverName),
        daysInYard: getField(item, FIELDS.daysInYard),
        vesselName: getField(item, FIELDS.vesselName),
      };

      if (terms.length > 0) {
        const haystack = [
          displayDate(row.gateOut),
          row.gateOutNo,
          row.containerNo,
          row.transport,
          row.bookingNo,
          row.truckNo,
          row.driverName,
          row.daysInYard,
          row.vesselName,
        ]
          .join(" ")
          .toLowerCase();
        if (!terms.every((t) => haystack.includes(t))) return;
      }

      if (!map.has(gateOut)) map.set(gateOut, []);
      map.get(gateOut).push(row);
    });

    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, rows]) => ({
        date,
        rows: rows.sort((a, b) => Number(a.gateOutNo) - Number(b.gateOutNo)),
      }));
  }, [analyticsData, fromdate, todate, searchText]);

  const thStyle = {
    textAlign: "left",
    fontWeight: 400,
    textDecoration: "underline",
    fontSize: 11,
    padding: "4px 8px",
    whiteSpace: "nowrap",
    verticalAlign: "bottom",
  };
  const tdStyle = {
    fontSize: 11,
    padding: "3px 8px",
    whiteSpace: "nowrap",
  };

  return (
    <div ref={filterRef}>
      {/* ---------- Date selector + search ---------- */}
      <div className="align__date_1">
        <div
          onClick={() => setActiveDates(!activeDates)}
          className="cursor-pointer flex items-center gap-2"
        >
          <h2 className="text-2xl">
            <BsCalendar2Date />
          </h2>
          <span className="bg-white inline-block px-1 rounded">
            {displayDate(fromdate).replace(/\//g, "-")}
          </span>{" "}
          -{" "}
          <span className="bg-white inline-block px-1 rounded">
            {displayDate(todate).replace(/\//g, "-")}
          </span>
        </div>
        <div className="w-[200px]"> </div>

        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search container, transport, truck, driver, vessel..."
          style={{
            border: "1px solid #ccc",
            borderRadius: 4,
            padding: "4px 8px",
            fontSize: 13,
            minWidth: 300,
          }}
        />

        {activeDates && (
          <div className="date__custom">
            <div className="flex justify-between">
              <h1>Recommended</h1>
            </div>
            <button onClick={() => handleDateFilterChange("last3days")}>Last 3 Days</button>
            <button onClick={() => handleDateFilterChange("last7days")}>Last 7 Days</button>
            <button onClick={() => handleDateFilterChange("last30days")}>Last 30 Days</button>
            <h1>Calendar months</h1>
            <button onClick={() => handleDateFilterChange("lastMonth")}>Last Month</button>
            <button onClick={() => handleDateFilterChange("lastQuarter")}>Last Quarter</button>
            <button onClick={() => handleDateFilterChange("lastYear")}>Last Year</button>
            <h1>Custom date</h1>
            <main className="flex mx-1 gap-3">
              <div>
                <p>Start Date :</p>
                <input
                  type="date"
                  value={fromdate}
                  max={todate}
                  onChange={(e) => e.target.value && setFromdate(e.target.value)}
                />
              </div>
              <div>
                <p>End Date :</p>
                <input
                  type="date"
                  value={todate}
                  min={fromdate}
                  onChange={(e) => e.target.value && setTodate(e.target.value)}
                />
              </div>
            </main>
          </div>
        )}
      </div>

      {/* ---------- Report ---------- */}
      <section className="mt-5 px-4">
        {isLoading && <p className="text-sm">Loading...</p>}
        {error && !isLoading && <p className="text-sm text-red-600">{error}</p>}
        {invalidRange && !isLoading && (
          <p className="text-sm text-red-600">Start date cannot be after end date.</p>
        )}
        {!isLoading && !error && !invalidRange && groups.length === 0 && (
          <p className="text-sm">No records found for the selected dates.</p>
        )}

        {!isLoading &&
          !error &&
          !invalidRange &&
          groups.map(({ date, rows }) => (
            <div key={date} style={{ marginBottom: 32 }}>
              <h2
                style={{
                  color: "red",
                  fontWeight: 700,
                  fontSize: 20,
                  marginBottom: 8,
                }}
              >
                {displayDate(date)}
              </h2>

              <div className="overflow-x-auto">
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>GATEOUTDATE</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>GATEOUT NO</th>
                      <th style={thStyle}>CONTAINERNO</th>
                      <th style={thStyle}>TRANSPORT</th>
                      <th style={thStyle}>BOOKINGNO</th>
                      <th style={thStyle}>TRUCKNO</th>
                      <th style={thStyle}>DRIVERNAME</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>
                        DAYS
                        <br />
                        INYARD
                      </th>
                      <th style={thStyle}>VESSELNAME</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={`${r.gateOutNo}-${r.containerNo}-${i}`}>
                        <td style={tdStyle}>{displayDate(r.gateOut)}</td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>{r.gateOutNo}</td>
                        <td style={tdStyle}>{r.containerNo}</td>
                        <td style={tdStyle}>{r.transport}</td>
                        <td style={tdStyle}>{r.bookingNo}</td>
                        <td style={tdStyle}>{r.truckNo}</td>
                        <td style={tdStyle}>{r.driverName}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>{r.daysInYard}</td>
                        <td style={tdStyle}>{r.vesselName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
      </section>
    </div>
  );
};

export default DepotGateInwards;
